import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import {
  Globe,
  TrendingUp,
  Settings,
  Scale,
  Zap,
  ArrowRight,
  Clock,
  User,
} from "lucide-react";

const BlogPage = () => {
  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/franjo-vucic-d0pg/30min",
      });
    }
  };

  const featuredPost = {
    title: "Entering European Markets: Operational Considerations",
    excerpt:
      "A comprehensive guide to the operational infrastructure needed when expanding your business into European markets. Learn about compliance, entity setup, and local requirements.",
    category: "European Expansion",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=400&fit=crop",
  };

  const blogPosts = [
    {
      icon: TrendingUp,
      title: "Operational Scaling: Building Infrastructure for Growth",
      excerpt:
        "How to build scalable operational systems that support rapid company growth without breaking.",
      category: "Operations",
      readTime: "5 min read",
    },
    {
      icon: Settings,
      title: "Building Efficient Backoffice Systems",
      excerpt:
        "Best practices for creating efficient, automated backoffice operations that reduce overhead.",
      category: "Efficiency",
      readTime: "6 min read",
    },
    {
      icon: Scale,
      title: "Compliance for International Companies",
      excerpt:
        "Navigating regulatory requirements when operating across multiple European jurisdictions.",
      category: "Compliance",
      readTime: "7 min read",
    },
    {
      icon: Zap,
      title: "Automation in Operations: Where to Start",
      excerpt:
        "Identifying the highest-impact opportunities for automation in your operational workflows.",
      category: "Technology",
      readTime: "4 min read",
    },
    {
      icon: Globe,
      title: "The Nearshore Advantage for European Operations",
      excerpt:
        "Why nearshore operational partners offer unique benefits for companies operating in Europe.",
      category: "Strategy",
      readTime: "5 min read",
    },
  ];

  const categories = [
    "All",
    "European Expansion",
    "Operations",
    "Compliance",
    "Technology",
    "Strategy",
  ];

  return (
    <div data-testid="blog-page" className="pt-16 md:pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#F5F7FA] to-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
              Blog & Insights
            </p>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight leading-[1.1] text-[#0B2A4A] mb-6 font-['Sora']">
              Insights & Resources
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600">
              Thought leadership and practical guides on operational excellence, 
              European expansion, and building efficient business infrastructure.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card
            data-testid="featured-post"
            className="overflow-hidden bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(6,81,237,0.1)] hover:shadow-[0_8px_30px_-4px_rgba(6,81,237,0.15)] transition-all duration-300 cursor-pointer group"
          >
            <CardContent className="p-0">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="relative h-64 lg:h-auto">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#2F6FED] text-white text-xs font-medium px-3 py-1 rounded-full">
                      Featured
                    </span>
                  </div>
                </div>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-sm text-[#2F6FED] font-medium">
                      {featuredPost.category}
                    </span>
                    <span className="text-sm text-slate-400 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {featuredPost.readTime}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-semibold text-[#0B2A4A] mb-4 font-['Sora'] group-hover:text-[#2F6FED] transition-colors">
                    {featuredPost.title}
                  </h2>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#F5F7FA] rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#0B2A4A]">VTZ Team</p>
                      <p className="text-xs text-slate-500">Operations Experts</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3">
            {categories.map((category, index) => (
              <button
                key={index}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  index === 0
                    ? "bg-[#0B2A4A] text-white"
                    : "bg-[#F5F7FA] text-slate-600 hover:bg-slate-200"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <Card
                key={index}
                data-testid={`blog-post-${index}`}
                className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer group hover:-translate-y-1"
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 bg-[#2F6FED]/10 rounded-lg flex items-center justify-center group-hover:bg-[#2F6FED]/20 transition-colors">
                      <post.icon className="w-5 h-5 text-[#2F6FED]" />
                    </div>
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                  </div>
                  <span className="text-xs text-[#2F6FED] font-medium uppercase tracking-wide">
                    {post.category}
                  </span>
                  <h3 className="text-lg font-semibold text-[#0B2A4A] mt-2 mb-3 font-['Sora'] group-hover:text-[#2F6FED] transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {post.excerpt}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 md:py-28 bg-[#F5F7FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
            Stay Updated
          </p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0B2A4A] mb-6 font-['Sora']">
            Subscribe to Our Insights
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Get the latest articles on operational excellence and European expansion 
            delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 h-12 px-4 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#2F6FED] focus:border-transparent"
            />
            <Button className="bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium px-6 py-3 rounded-md transition-all duration-200">
              Subscribe
            </Button>
          </div>
          <p className="text-sm text-slate-500 mt-4">
            No spam. Unsubscribe anytime.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#0B2A4A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6 font-['Sora']">
            Ready to Optimize Your Operations?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Schedule a consultation to discuss how VTZ Backoffice can support 
            your business growth.
          </p>
          <Button
            data-testid="blog-cta-book-call"
            onClick={openCalendly}
            className="bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium px-8 py-4 rounded-md transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Schedule a Call
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
